/**
 * Markets — the game board (docs/markets-ux.md). One soft card per game with its moneyline (one
 * best price + venue per side), futures with their three shortest prices, grouped into Live now /
 * Starting soon / Today / Tomorrow / Later. Prices load lazily per card as it nears the viewport;
 * tapping a price opens the trade ticket right here (a push drawer on desktop, the bottom sheet
 * on mobile) so the user keeps browsing.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import type { Contest, League, LiquidityPosition, PartnerLiquidity } from "@greenbook/client";
import {
  useAccountPartners,
  useContests,
  useContestStateBatch,
  useLeagues,
  type ContestFilter,
} from "../lib/queries.js";
import { VenueLogo } from "../components/VenueLogo.js";
import { ErrorState } from "../components/ui.js";
import { BottomSheet, Pill, SectionHeading, Skeleton, TicketDrawer } from "../components/kit.js";
import { GameCard, GameCardSkeleton, liveFromState, ViewWatcher, WatcherCtx, type StateEntry } from "../components/GameCard.js";
import { TradeTicket } from "../components/TradeTicket.js";
import { useLiveContest, useLiveEnabled } from "../lib/ws.js";
import { SCOPES, useAccess, useIsAuthed } from "../lib/omauth.js";
import { useTradingMode } from "../lib/venues.js";
import { useIsMobile } from "../lib/responsive.js";
import {
  bucketContests,
  isGame,
  startMs,
  teamLabel,
  teamsOf,
  visibleContests,
  type Bucket,
  type CardQuote,
  type LiveSignal,
  type PriceStatus,
} from "../lib/marketsBoard.js";

type StatusOpt = "open" | "closed" | "settled";

/** Contest list page size (the API max) and how many pages to pull before asking. */
const PAGE_SIZE = 200;
const AUTO_PAGES = 5;
/** Free plan: 25 streamed contests across connections (docs → Rate limits). */
const STREAM_CAP = 25;
/** Batch-read live state only for games that could plausibly be live. */
const STATE_WINDOW_BEFORE_MS = 12 * 3_600_000;
const STATE_WINDOW_AFTER_MS = 60 * 60_000;
const STATE_BATCH_CAP = 100;
/** Cards per section before "Show more" (Live now always shows every game). */
const SECTION_PAGE = 24;

interface TicketPick {
  contestId: string;
  position: LiquidityPosition;
  pl: PartnerLiquidity;
}

/* ------------------------------------------------------------------------ page */

export function ContestBrowser() {
  const [params, setParams] = useSearchParams();
  const leagueId = params.get("league") ?? "";
  const leaguesQ = useLeagues();
  const [status, setStatus] = useState<StatusOpt>("open");
  const [search, setSearch] = useState(() => params.get("q") ?? "");
  const [debouncedSearch, setDebouncedSearch] = useState(search.trim());
  const [tradeableOnly, setTradeableOnly] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [ticket, setTicket] = useState<TicketPick | null>(null);
  // How many cards each section shows; reset when the board's filters change.
  const [reveal, setReveal] = useState<Partial<Record<Bucket, number>>>({});
  const isMobile = useIsMobile();
  const authed = useIsAuthed();
  const mode = useTradingMode();

  const setParam = useCallback(
    (key: string, value: string) =>
      setParams(
        (p) => {
          const next = new URLSearchParams(p);
          if (value) next.set(key, value);
          else next.delete(key);
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );
  const setLeagueId = (id: string) => setParam("league", id);

  // Minute ticker so "in 20m" and the live/soon split stay fresh.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  // Debounce the search box before it hits the API (server-side title search), so the full
  // catalog is searched — not just the pages already loaded. Mirrored into ?q=.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setParam("q", search.trim());
    }, 300);
    return () => clearTimeout(t);
  }, [search, setParam]);
  // `q` needs 2–100 chars; below 2 we omit it and show the full (filtered) list.
  const serverQ = debouncedSearch.length >= 2 ? debouncedSearch.slice(0, 100) : undefined;
  const filter: ContestFilter = { leagueId: leagueId || undefined, status, q: serverQ, limit: PAGE_SIZE };
  const contestsQ = useContests(filter);
  // Counts for the league pills come from the all-leagues list (same query when "All" is on).
  const allQ = useContests({ status, q: serverQ, limit: PAGE_SIZE });
  useAutoPages(contestsQ);
  useAutoPages(allQ);

  const leagueById = useMemo(() => {
    const m = new Map<string, League>();
    for (const l of leaguesQ.data ?? []) m.set(l.id, l);
    return m;
  }, [leaguesQ.data]);

  const contests: Contest[] = useMemo(() => (contestsQ.data?.pages ?? []).flatMap((p) => p.data), [contestsQ.data]);
  const allContests: Contest[] = useMemo(() => (allQ.data?.pages ?? []).flatMap((p) => p.data), [allQ.data]);

  // Instant local refinement of the loaded (server-filtered) rows — also matches team names and
  // league, which the server's title-only `q` doesn't.
  const needle = search.trim().toLowerCase();
  const matches = useCallback(
    (c: Contest): boolean => {
      if (!needle) return true;
      const t = teamsOf(c);
      const hay = [c.title, t ? `${teamLabel(t.home)} ${teamLabel(t.away)}` : "", leagueById.get(c.league_id ?? "")?.name ?? ""]
        .join(" ")
        .toLowerCase();
      return hay.includes(needle);
    },
    [needle, leagueById],
  );

  // Each card reports its price status (pending / tradeable / empty / error) once known.
  const [statuses, setStatuses] = useState<Map<string, PriceStatus>>(() => new Map());
  const onStatus = useCallback((id: string, s: PriceStatus) => {
    setStatuses((m) => (m.get(id) === s ? m : new Map(m).set(id, s)));
  }, []);
  const statusOf = useCallback(
    (c: Contest): PriceStatus => (c.has_markets === false ? "empty" : (statuses.get(c.id) ?? "pending")),
    [statuses],
  );

  const shown = useMemo(
    () => visibleContests(contests.filter(matches), statusOf, tradeableOnly),
    [contests, matches, statusOf, tradeableOnly],
  );

  // Live state: only for games near their start (a batch of 600 ids would be wasteful).
  const stateAccess = useAccess(SCOPES.gamestate).ok;
  const stateIds = useMemo(
    () =>
      contests
        .filter((c) => {
          if (!isGame(c)) return false;
          const s = startMs(c);
          return Number.isFinite(s) && s >= now - STATE_WINDOW_BEFORE_MS && s <= now + STATE_WINDOW_AFTER_MS;
        })
        .slice(0, STATE_BATCH_CAP)
        .map((c) => c.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contests, Math.floor(now / 300_000)],
  );
  const statesQ = useContestStateBatch(stateIds);
  const stateOf = useCallback(
    (id: string): StateEntry | undefined => statesQ.data?.[id] as StateEntry | undefined,
    [statesQ.data],
  );
  const liveOf = useCallback(
    (c: Contest): LiveSignal => (stateAccess ? liveFromState(stateOf(c.id)) : null),
    [stateAccess, stateOf],
  );

  const sections = useMemo(() => bucketContests(shown, liveOf, now), [shown, liveOf, now]);
  useEffect(() => setReveal({}), [leagueId, status, debouncedSearch, tradeableOnly]);

  // League pills: ALL active leagues from the leagues endpoint (so off-first-page leagues always
  // appear), each with how many of its games are on the board right now.
  const leagueRail = useMemo(() => {
    const n = new Map<string, number>();
    for (const c of visibleContests(allContests.filter(matches), statusOf, tradeableOnly)) {
      n.set(c.league_id ?? "", (n.get(c.league_id ?? "") ?? 0) + 1);
    }
    return (leaguesQ.data ?? [])
      .filter((l) => (l.status ?? "active") === "active")
      .map((l) => ({ id: l.id, n: n.get(l.id) ?? 0, league: l }))
      .sort((a, b) => (a.league.name ?? "").localeCompare(b.league.name ?? ""));
  }, [allContests, matches, statusOf, tradeableOnly, leaguesQ.data]);
  const allCount = useMemo(
    () => visibleContests(allContests.filter(matches), statusOf, tradeableOnly).length,
    [allContests, matches, statusOf, tradeableOnly],
  );

  // Streaming: the on-screen cards (in board order), capped at the plan's 25 watched contests.
  const visibleSet = useRef(new Set<string>());
  const [visibleTick, setVisibleTick] = useState(0);
  const tickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onVisible = useCallback((id: string, v: boolean) => {
    const s = visibleSet.current;
    if (v === s.has(id)) return;
    if (v) s.add(id);
    else s.delete(id);
    if (tickTimer.current) clearTimeout(tickTimer.current);
    tickTimer.current = setTimeout(() => setVisibleTick((t) => t + 1), 250);
  }, []);
  useEffect(() => () => void (tickTimer.current && clearTimeout(tickTimer.current)), []);
  const boardOrder = useMemo(() => sections.flatMap((s) => s.contests.map((c) => c.id)), [sections]);
  const streamIds = useMemo(
    () => boardOrder.filter((id) => visibleSet.current.has(id)).slice(0, STREAM_CAP),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [boardOrder, visibleTick],
  );
  const liveEnabled = useLiveEnabled();
  useLiveContest(streamIds);
  const streamed = useMemo(() => new Set(liveEnabled ? streamIds : []), [liveEnabled, streamIds]);

  // Real mode: best price among the user's connected venues (same scope as the game page).
  const partnersQ = useAccountPartners();
  const allowed = useMemo(() => {
    if (mode !== "real" || !authed) return null;
    const s = new Set<string>();
    for (const ap of partnersQ.data?.account_partners ?? []) {
      if (ap.status === "active" && ap.credential_status === "connected") s.add(ap.partner_id);
    }
    return s.size > 0 ? s : null;
  }, [mode, authed, partnersQ.data]);

  // The scroll container roots the viewport watcher.
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const watcher = useMemo(() => (scrollEl ? new ViewWatcher(scrollEl) : null), [scrollEl]);
  useEffect(() => () => watcher?.disconnect(), [watcher]);

  const pick = useCallback((contest: Contest, q: CardQuote) => {
    if (!q.best) return;
    setTicket({ contestId: contest.id, position: q.position as LiquidityPosition, pl: q.best as PartnerLiquidity });
  }, []);
  const closeTicket = useCallback(() => setTicket(null), []);

  // Opening the drawer narrows the board; keep the card the user tapped in view.
  useEffect(() => {
    if (!ticket || isMobile || !scrollEl) return;
    const el = scrollEl.querySelector(`[data-contest="${CSS.escape(ticket.contestId)}"]`);
    requestAnimationFrame(() => el?.scrollIntoView({ block: "nearest" }));
  }, [ticket, isMobile, scrollEl]);

  const activeFilterCount = (status !== "open" ? 1 : 0) + (tradeableOnly ? 0 : 1);
  const resetFilters = () => {
    setStatus("open");
    setTradeableOnly(true);
  };
  const league = leagueById.get(leagueId);
  const hiddenByTradeable = tradeableOnly && contests.filter(matches).length > shown.length;
  const stillLoading = contestsQ.isFetchingNextPage || (contestsQ.hasNextPage && (contestsQ.data?.pages.length ?? 0) < AUTO_PAGES);

  const filters = (
    <FiltersPanel
      status={status}
      onStatus={setStatus}
      tradeableOnly={tradeableOnly}
      onTradeable={setTradeableOnly}
      onReset={activeFilterCount > 0 ? resetFilters : undefined}
    />
  );

  return (
    <div className="flex h-full flex-col">
      {/* filter row — sticky by construction: it sits above the board's own scroll */}
      <div className="shrink-0 border-b border-border bg-panel">
        <div className="flex flex-col gap-2 px-4 py-2.5 lg:flex-row lg:items-center lg:gap-3">
          <div className="flex items-center gap-2 lg:contents">
            <SearchBox value={search} onChange={setSearch} />
            <div className="lg:hidden">
              <FiltersPill count={activeFilterCount} onClick={() => setFiltersOpen(true)} />
            </div>
          </div>
          <div className="-mx-4 flex min-w-0 flex-1 items-center gap-2 overflow-x-auto px-4 lg:mx-0 lg:px-0" role="toolbar" aria-label="Leagues">
            <Pill active={leagueId === ""} onClick={() => setLeagueId("")} count={allCount}>
              All
            </Pill>
            {leagueRail.map(({ id, n, league: l }) => (
              <Pill
                key={id}
                active={leagueId === id}
                onClick={() => setLeagueId(id)}
                count={n}
                icon={<VenueLogo name={l.name} logoUrl={l.image_url} size={14} />}
              >
                {l.name ?? id}
              </Pill>
            ))}
          </div>
          <div className="relative hidden lg:block">
            <FiltersPill count={activeFilterCount} onClick={() => setFiltersOpen((v) => !v)} />
            {filtersOpen && !isMobile && <Popover onClose={() => setFiltersOpen(false)}>{filters}</Popover>}
          </div>
        </div>
      </div>

      {isMobile && (
        <BottomSheet open={filtersOpen} onClose={() => setFiltersOpen(false)} label="Filters" header={<SheetTitle>Filters</SheetTitle>}>
          <div className="overflow-y-auto px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-4">
            {filters}
            <button
              type="button"
              onClick={() => setFiltersOpen(false)}
              className="mt-5 min-h-12 w-full rounded-lg border border-amber/60 bg-amber/15 text-sm font-semibold text-amber active:bg-amber/25"
            >
              Show {shown.length} game{shown.length === 1 ? "" : "s"}
            </button>
          </div>
        </BottomSheet>
      )}

      <div className="flex min-h-0 flex-1">
        <div ref={setScrollEl} className="@container min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
          <WatcherCtx.Provider value={watcher}>
            {contestsQ.isLoading ? (
              <BoardSkeleton />
            ) : contestsQ.isError ? (
              <div className="p-4">
                <ErrorState error={contestsQ.error} onRetry={() => contestsQ.refetch()} />
              </div>
            ) : shown.length === 0 && stillLoading ? (
              <BoardSkeleton />
            ) : shown.length === 0 ? (
              <BoardEmpty
                search={needle ? search.trim() : null}
                leagueName={leagueId ? (league?.name ?? "these") : null}
                status={status}
                hiddenByTradeable={hiddenByTradeable}
                onClearSearch={() => setSearch("")}
                onAllLeagues={() => setLeagueId("")}
                onShowAll={() => setTradeableOnly(false)}
              />
            ) : (
              <div className="grid grid-cols-1 gap-3 p-4 @2xl:grid-cols-2 @6xl:grid-cols-3">
                {sections.map((s) => (
                  <Section
                    key={s.key}
                    bucket={s.key}
                    label={s.label}
                    count={s.contests.length}
                    shownCount={s.key === "live" ? s.contests.length : (reveal[s.key] ?? SECTION_PAGE)}
                    onMore={() => setReveal((r) => ({ ...r, [s.key]: (r[s.key] ?? SECTION_PAGE) + SECTION_PAGE }))}
                  >
                    {s.contests.slice(0, s.key === "live" ? undefined : (reveal[s.key] ?? SECTION_PAGE)).map((c) => (
                      <GameCard
                        key={c.id}
                        contest={c}
                        bucket={s.key}
                        league={leagueById.get(c.league_id ?? "")}
                        state={stateOf(c.id)}
                        now={now}
                        allowed={allowed}
                        tradeableOnly={tradeableOnly}
                        streamed={streamed.has(c.id)}
                        activeHash={ticket?.contestId === c.id ? ticket.position.position_hash : null}
                        onPick={pick}
                        onStatus={onStatus}
                        onVisible={onVisible}
                      />
                    ))}
                  </Section>
                ))}
                <div className="col-span-full flex items-center justify-center py-2 text-[11px] text-faint">
                  {contestsQ.isFetchingNextPage ? (
                    "Loading more games…"
                  ) : contestsQ.hasNextPage ? (
                    <button
                      type="button"
                      onClick={() => contestsQ.fetchNextPage()}
                      className="min-h-11 rounded-full border border-border-2 px-4 text-[13px] text-ink hover:bg-raised"
                    >
                      Load more
                    </button>
                  ) : (
                    <span>
                      {shown.length} game{shown.length === 1 ? "" : "s"}
                    </span>
                  )}
                </div>
              </div>
            )}
          </WatcherCtx.Provider>
        </div>

        {/* The ticket: a push drawer on desktop (never covers a price, incl. the tapped one), the
            bottom sheet on mobile. */}
        <TicketDrawer open={!!ticket} onClose={closeTicket} mobile={isMobile} header={<SheetTitle>Trade ticket</SheetTitle>}>
          {ticket && (
            <TradeTicket
              key={`${ticket.position.position_hash}|${ticket.pl.partner_id}`}
              position={ticket.position}
              pl={ticket.pl}
              initialMode={mode}
              onClose={closeTicket}
            />
          )}
        </TicketDrawer>
      </div>
    </div>
  );
}

/** Keep pulling pages (200 each) up to AUTO_PAGES so sections are complete, not page-1-only. */
function useAutoPages(q: ReturnType<typeof useContests>) {
  const pages = q.data?.pages.length ?? 0;
  const { hasNextPage, isFetching, isError, fetchNextPage } = q;
  useEffect(() => {
    // `isFetching` (not just isFetchingNextPage): calling fetchNextPage mid-fetch cancels it.
    if (hasNextPage && !isFetching && !isError && pages > 0 && pages < AUTO_PAGES) {
      void fetchNextPage({ cancelRefetch: false });
    }
  }, [hasNextPage, isFetching, isError, pages, fetchNextPage]);
}

/* ---------------------------------------------------------------- filter bits */

function SearchBox({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="relative min-w-0 flex-1 lg:w-64 lg:flex-none">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[12px] text-faint" aria-hidden>
        ⌕
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search games"
        aria-label="Search games"
        type="search"
        className="min-h-11 w-full rounded-full border border-border-2 bg-panel-2 py-2 pl-8 pr-3 text-[13px] text-ink outline-none placeholder:text-faint focus:border-amber lg:min-h-8 lg:py-1.5"
      />
    </div>
  );
}

function FiltersPill({ count, onClick }: { count: number; onClick: () => void }) {
  return (
    <Pill active={count > 0} onClick={onClick} aria-label={`Filters${count ? ` (${count} changed)` : ""}`}>
      Filters
      {count > 0 && (
        <span className="ml-1.5 inline-grid h-4 min-w-4 place-items-center rounded-full bg-amber px-1 text-[10px] font-semibold leading-none text-bg">
          {count}
        </span>
      )}
    </Pill>
  );
}

function Popover({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.parentElement?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);
  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Filters"
      className="gb-fade absolute right-0 top-full z-30 mt-2 w-72 rounded-lg border border-border-2 bg-panel p-4 shadow-xl"
    >
      {children}
    </div>
  );
}

function FiltersPanel({
  status,
  onStatus,
  tradeableOnly,
  onTradeable,
  onReset,
}: {
  status: StatusOpt;
  onStatus: (s: StatusOpt) => void;
  tradeableOnly: boolean;
  onTradeable: (v: boolean) => void;
  onReset?: () => void;
}) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-dim">Status</span>
        {onReset && (
          <button type="button" onClick={onReset} className="py-1 text-[12px] text-amber hover:underline">
            Reset
          </button>
        )}
      </div>
      <div className="flex gap-2">
        {(["open", "closed", "settled"] as StatusOpt[]).map((s) => (
          <Pill key={s} active={status === s} onClick={() => onStatus(s)} className="flex-1 justify-center capitalize">
            {s}
          </Pill>
        ))}
      </div>
      <label className="mt-4 flex min-h-11 cursor-pointer items-center justify-between gap-3">
        <span className="flex flex-col">
          <span className="text-[13px] text-ink">Tradeable only</span>
          <span className="text-[11px] text-faint">Hide games with no price to trade</span>
        </span>
        <input
          type="checkbox"
          checked={tradeableOnly}
          onChange={(e) => onTradeable(e.target.checked)}
          className="h-5 w-5 accent-amber"
        />
      </label>
    </div>
  );
}

function SheetTitle({ children }: { children: ReactNode }) {
  return <span className="text-[11px] font-semibold uppercase tracking-wider text-dim">{children}</span>;
}

/* -------------------------------------------------------------------- board */

function Section({
  bucket,
  label,
  count,
  shownCount,
  onMore,
  children,
}: {
  bucket: Bucket;
  label: string;
  count: number;
  shownCount: number;
  onMore: () => void;
  children: ReactNode;
}) {
  const left = count - shownCount;
  return (
    <>
      <SectionHeading title={label} count={count} tone={bucket === "live" ? "live" : "default"} />
      {children}
      {left > 0 && (
        <div className="col-span-full flex justify-center">
          <button
            type="button"
            onClick={onMore}
            className="min-h-11 rounded-full border border-border-2 bg-panel-2 px-4 text-[13px] text-dim hover:bg-raised hover:text-ink lg:min-h-9"
          >
            Show {Math.min(SECTION_PAGE, left)} more {label.toLowerCase()} · {left} left
          </button>
        </div>
      )}
    </>
  );
}

function BoardEmpty({
  search,
  leagueName,
  status,
  hiddenByTradeable,
  onClearSearch,
  onAllLeagues,
  onShowAll,
}: {
  search: string | null;
  leagueName: string | null;
  status: StatusOpt;
  hiddenByTradeable: boolean;
  onClearSearch: () => void;
  onAllLeagues: () => void;
  onShowAll: () => void;
}) {
  const title = search
    ? `No games match “${search}”`
    : leagueName
      ? `No ${status} ${leagueName} games right now`
      : `No ${status} games right now`;
  const action = search
    ? { label: "Clear search", onClick: onClearSearch }
    : leagueName
      ? { label: "Show all leagues", onClick: onAllLeagues }
      : null;
  return (
    <div className="flex justify-center p-4 sm:p-8">
      <div className="w-full max-w-md rounded-lg border border-border bg-panel-2 p-6 text-center">
        <div className="text-[14px] text-ink">{title}</div>
        {hiddenByTradeable && (
          <div className="mt-1 text-[12px] text-faint">
            Some games are hidden because they have no prices.{" "}
            <button type="button" onClick={onShowAll} className="text-amber hover:underline">
              Show them
            </button>
          </div>
        )}
        {action && (
          <button
            type="button"
            onClick={action.onClick}
            className="mt-4 min-h-11 rounded-full border border-amber/60 bg-amber/15 px-5 text-[13px] font-semibold text-amber hover:bg-amber/25"
          >
            {action.label}
          </button>
        )}
      </div>
    </div>
  );
}

function BoardSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-3 p-4 @2xl:grid-cols-2 @6xl:grid-cols-3" aria-busy="true" aria-label="Loading games">
      <div className="col-span-full pb-0.5">
        <Skeleton className="h-3.5 w-24" />
      </div>
      {Array.from({ length: 8 }).map((_, i) => (
        <GameCardSkeleton key={i} />
      ))}
    </div>
  );
}
